import { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { RefreshCw, Edit } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAbility } from '@/components/authnz/AbilityContext';
import { Product } from './types/product';
import { Price } from './types/price';
import { subscribeToProducts } from './lib/product-database';
import { subscribeToAllPrices } from './lib/price-database';
import { buildProductPriceRows, formatPriceAmount, formatPriceInterval } from './lib/product-price-rows';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import logger from '@/lib/logging';
import { syncProductsFromStripeApi } from '@/lib/subscriptions-admin-api';
import { UserRole } from '@/components/authnz/types/rbac';
import { ContextualToolbar, ToolbarButtonGroup } from '@/components/ui/contextual-toolbar';
import { format } from 'date-fns';
import ProductForm from './ProductForm';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

export default function ProductManagement() {
  const [products, setProducts] = useState<Product[]>([]);
  const [prices, setPrices] = useState<Price[]>([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [showForm, setShowForm] = useState(false);
  const { toast } = useToast();
  const ability = useAbility();

  useEffect(() => {
    const unsubscribe = subscribeToProducts(
      (productsData) => {
        setProducts(productsData);
        setLoading(false);
      },
      (error) => {
        logger.error('Error subscribing to products:', error);
        toast({
          title: 'Error',
          description: 'Failed to load products',
          variant: 'destructive',
        });
        setLoading(false);
      }
    );

    const unsubscribePrices = subscribeToAllPrices(setPrices, (error) => {
      logger.error('Error subscribing to prices:', error);
      toast({
        title: 'Error',
        description: 'Failed to load prices',
        variant: 'destructive',
      });
    });

    return () => {
      unsubscribe();
      unsubscribePrices();
    };
  }, [toast]);

  const rows = useMemo(
    () => buildProductPriceRows(products, prices, { showArchived }),
    [products, prices, showArchived]
  );
  const hasArchived = useMemo(
    () => buildProductPriceRows(products, prices, { showArchived: true }).some((row) => row.archived),
    [products, prices]
  );

  const handleSyncFromStripe = async () => {
    setSyncing(true);
    try {
      const result = await syncProductsFromStripeApi(UserRole.STUDENT);
      const report = result.report;
      toast({
        title: 'Reconciled from Stripe',
        description: report
          ? `Products +${report.products.added} ~${report.products.updated} deactivated ${report.products.deactivated}; prices +${report.prices.added} ~${report.prices.updated} deactivated ${report.prices.deactivated}`
          : 'Stripe reconcile completed',
      });
    } catch (error: any) {
      logger.error('Error syncing products:', error);
      toast({
        title: 'Sync Failed',
        description: error.message || 'Failed to sync products from Stripe',
        variant: 'destructive',
      });
    } finally {
      setSyncing(false);
    }
  };

  const canManage = ability.can('manage', 'Product');

  return (
    <div className="flex flex-col gap-6 w-full">
      <ContextualToolbar position="sticky-top">
        <ToolbarButtonGroup align="right">
          {/* Products and prices are created in the Stripe Dashboard, then mirrored here. */}
          {canManage && (
            <Button
              variant="outline"
              size="sm"
              className="h-10"
              onClick={handleSyncFromStripe}
              disabled={syncing}
            >
              <RefreshCw className={`h-4 w-4 sm:mr-2 ${syncing ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Reconcile from Stripe</span>
            </Button>
          )}
        </ToolbarButtonGroup>
      </ContextualToolbar>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0">
          <CardTitle>Products</CardTitle>
          {hasArchived && (
            <div className="flex items-center gap-2">
              <Switch id="show-archived-prices" checked={showArchived} onCheckedChange={setShowArchived} />
              <Label htmlFor="show-archived-prices" className="text-sm text-muted-foreground">
                Show archived
              </Label>
            </div>
          )}
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No products found</p>
              {canManage && (
                <Button
                  variant="outline"
                  className="mt-4"
                  onClick={handleSyncFromStripe}
                  disabled={syncing}
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
                  Sync from Stripe
                </Button>
              )}
            </div>
          ) : (
            <TooltipProvider>
              <div className="overflow-x-auto touch-pan-xy">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Price</TableHead>
                      <TableHead>Interval</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Status</TableHead>
                      {canManage && <TableHead className="text-right">Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((row) => (
                      <TableRow key={row.key} className={row.archived ? 'text-muted-foreground' : undefined}>
                        <TableCell className="font-medium">{row.product.name}</TableCell>
                        <TableCell className="max-w-md truncate">{row.product.description}</TableCell>
                        <TableCell>
                          {formatPriceAmount(row.amount, row.currency)}
                        </TableCell>
                        <TableCell>
                          <Badge variant={row.interval === 'one_time' ? 'secondary' : 'outline'}>
                            {formatPriceInterval(row.interval, row.intervalCount)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{row.product.userRole}</Badge>
                        </TableCell>
                        <TableCell>
                          {row.archived ? (
                            <Badge variant="outline">Archived</Badge>
                          ) : (
                            <Badge variant="secondary">Active</Badge>
                          )}
                        </TableCell>
                        {canManage && (
                          <TableCell className="text-right">
                            {row.isFirstOfProduct && (
                              <div className="flex justify-end gap-2">
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      aria-label={`Edit role for ${row.product.name}`}
                                      onClick={() => {
                                        setEditingProduct(row.product);
                                        setShowForm(true);
                                      }}
                                    >
                                      <Edit className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Edit role</p>
                                  </TooltipContent>
                                </Tooltip>
                              </div>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </TooltipProvider>
          )}
        </CardContent>
      </Card>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit product role</DialogTitle>
          </DialogHeader>
          {editingProduct && (
            <ProductForm
              product={editingProduct}
              onSuccess={() => {
                setShowForm(false);
                setEditingProduct(null);
              }}
              onCancel={() => {
                setShowForm(false);
                setEditingProduct(null);
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

